import {
  ClientFrontlineConfig,
  DoorType,
  EquipmentXLQuestionKey,
  EquipmentQuestionKey,
  EquipmentCategory
} from '../../../../types/types'
import * as i18n from 'i18next'
import { sapEquipmentCodes, escalatorEquipmentSAPCodes, elevatorEquipmentSAPCodes } from '../../../common/common-utils'
import { CommercialTermRow } from 'app/types/tables'

export const getDoorTypeOptions = (frontlineConfig: ClientFrontlineConfig) =>
  getEnabledDoorTypes(frontlineConfig).map(c => ({
    value: c,
    label: i18n.t(`assessmentQuestions:doorType.choices.${c}`)
  }))

export const isElevatorsEnabledByFrontline = (frontlineConfig: ClientFrontlineConfig): boolean =>
  frontlineConfig.enabledEquipment.find(ee => elevatorEquipmentSAPCodes.includes(ee)) != null

export const isEscalatorsEnabledByFrontline = (frontlineConfig: ClientFrontlineConfig): boolean =>
  frontlineConfig.enabledEquipment.find(ee => escalatorEquipmentSAPCodes.includes(ee)) != null

export const isDoorsEnabledByFrontline = (frontlineConfig: ClientFrontlineConfig): boolean =>
  frontlineConfig.enabledEquipment.includes(sapEquipmentCodes.door)

export const isResiflowEnabledByFrontline = (frontlineConfig: ClientFrontlineConfig): boolean =>
  frontlineConfig.enabledEquipment.includes(sapEquipmentCodes.resiflow)

export const isEquipmentCategoryEnabledByFrontline = (
  frontlineConfig: ClientFrontlineConfig,
  eq: EquipmentCategory,
  isDoorsOpportunity: boolean
) => {
  switch (eq) {
    case 'elevator':
      return !isDoorsOpportunity && isElevatorsEnabledByFrontline(frontlineConfig)
    case 'escalator':
      return !isDoorsOpportunity && isEscalatorsEnabledByFrontline(frontlineConfig)
    case 'door':
      return isDoorsOpportunity && isDoorsEnabledByFrontline(frontlineConfig)
    case 'resiflow':
      return !isDoorsOpportunity && isResiflowEnabledByFrontline(frontlineConfig)
  }
}

export const getEnabledDoorTypes = (frontlineConfig: ClientFrontlineConfig): DoorType[] => {
  const typeConfig = frontlineConfig.doorQuestions.find(question => question.key === 'doorType')
  return typeConfig && typeConfig.choices ? (typeConfig.choices as DoorType[]) : []
}

export const getFrontlineEnabledTechnicalPlatforms = (frontlineConfig: ClientFrontlineConfig): string[] =>
  Object.keys(frontlineConfig.technicalPlatforms)

export const getFrontlineEnabledPeopleflowTechnicalPlatforms = (frontlineConfig: ClientFrontlineConfig): string[] =>
  Object.keys(frontlineConfig.peopleflowTechnicalPlatforms)

export const getFrontlineEnabledEscalatorTypes = (frontlineConfig: ClientFrontlineConfig): string[] =>
  Object.keys(frontlineConfig.escalatorTypes)

// We have different configurations for xl flow assessment questions
// but we don't want to handle them separately after assessment wizard
export function translateXLAssessmentQuestionAnswerKey(questionKey: EquipmentXLQuestionKey): EquipmentQuestionKey {
  switch (questionKey) {
    case 'CRITICALITY_FOR_CUSTOMERS_REASON_XL_ELEVATOR':
      return 'CRITICALITY_FOR_CUSTOMERS_REASON'
    case 'CRITICALITY_FOR_CUSTOMERS_REASON_XL_ESCALATOR':
      return 'CRITICALITY_FOR_CUSTOMERS_REASON_ESCALATOR'
    case 'CRITICALITY_FOR_CUSTOMERS_REASON_XL_DOOR':
      return 'CRITICALITY_FOR_CUSTOMERS_REASON_DOOR'
    case 'STARTS_PER_YEAR_XL':
      return 'STARTS_PER_YEAR'
    default:
      return questionKey
  }
}

export const isBillingPlanCommercialTermsConfiguredInMBS = (frontlineCommercialTerms: CommercialTermRow[]) =>
  frontlineCommercialTerms.some(ct => ct.key === 'KCSM_BILLING_PLAN')

export const isAUGRUCommercialTermsConfiguredInMBS = (frontlineCommercialTerms: CommercialTermRow[]) =>
  frontlineCommercialTerms.some(ct => ct.key === 'AUGRU')
